/**
 * The team, quickly: who is here, who can open the till, and the handful of
 * changes a manager makes on the spot.
 *
 * Rendered by the Team panel in every app's top bar, and by Cashier's
 * Settings → Cashiers (preset "till"). The full Users page in My stays the
 * place for everything that needs a form: branches, per-app access, invites,
 * removing someone. This covers the moments a manager is standing at the
 * counter with a phone: set a PIN, switch someone off, make someone a manager.
 *
 * Quick changes are deliberately the safe subset:
 *   - nobody changes their own role or switches themselves off,
 *   - the owner's row is read-only,
 *   - promoting to admin also grants every branch and app, as My's form does,
 *   - demoting an admin is NOT offered here: a scoped role needs its apps and
 *     branches chosen, and doing that without the form would leave the person
 *     with no app access at all. That link goes to the full page instead.
 * The backend enforces the same permissions; this only avoids offering what
 * it would refuse.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ExternalLink, Loader2, Mail, Plus, Search, UserCircle2 } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Skeleton } from '../ui/skeleton'
import { Switch } from '../ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import MemberActions from './MemberActions'
import PosPinDialog from './PosPinDialog'
import AddCashierDialog from './AddCashierDialog'
import {
  MY_USERS_URL, ORG_WIDE_ROLES, canOpenTill, teamApi,
  type RoleOption, type TeamBranch, type TeamMember,
} from './teamModel'

type Filter = 'all' | 'till' | 'inactive'

const FALLBACK_ROLES: RoleOption[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'content_manager', label: 'Content Manager' },
  { value: 'branch_manager', label: 'Branch Manager' },
  { value: 'viewer', label: 'Viewer' },
]

export default function TeamList({
  preset = 'all', offerAddCashier = false, showFullPageLink = true,
}: {
  /** Which filter it opens on. "till" = people who can open the register. */
  preset?: Filter
  /** Show "Add cashier" (name-only account with a minted PIN). */
  offerAddCashier?: boolean
  /** Link to My's full Users page. Off inside My itself. */
  showFullPageLink?: boolean
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { user: me } = useAuth()
  const canEdit = can('team:edit')
  const canInvite = can('team:invite')

  const [members, setMembers] = useState<TeamMember[] | null>(null)
  const [branches, setBranches] = useState<TeamBranch[]>([])
  const [roles, setRoles] = useState<RoleOption[]>(FALLBACK_ROLES)
  const [filter, setFilter] = useState<Filter>(preset)
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [pinFor, setPinFor] = useState<TeamMember | null>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    try {
      const [list, cat, br] = await Promise.all([
        teamApi.list(),
        teamApi.accessCatalog().catch(() => null),
        teamApi.branches().catch(() => [] as TeamBranch[]),
      ])
      setMembers(list)
      setBranches(br)
      if (cat?.roles) setRoles(cat.roles.filter(r => r.assignable !== false))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('team.loadFailed', 'Could not load the team.'))
      setMembers([])
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (members ?? [])
      .filter(m => filter === 'all' || (filter === 'till' ? canOpenTill(m) : !m.is_active))
      .filter(m => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name))
  }, [members, filter, search])

  const counts = useMemo(() => ({
    all: members?.length ?? 0,
    till: (members ?? []).filter(canOpenTill).length,
    inactive: (members ?? []).filter(m => !m.is_active).length,
  }), [members])

  const replace = (next: TeamMember) =>
    setMembers(prev => (prev ?? []).map(m => (m.id === next.id ? { ...m, ...next } : m)))

  const setActive = async (m: TeamMember, is_active: boolean) => {
    setBusyId(m.id)
    try {
      replace(await teamApi.update(m.id, { is_active }))
      toast.success(is_active
        ? t('team.activated', '{{name}} can sign in again.', { name: m.name })
        : t('team.deactivated', '{{name}} is switched off.', { name: m.name }))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('team.saveFailed', 'Could not save that change.'))
    } finally {
      setBusyId(null)
    }
  }

  const setRole = async (m: TeamMember, role: string) => {
    if (role === m.role) return
    setBusyId(m.id)
    try {
      // Promoting to an org-wide office grants every branch and app, exactly
      // as My's edit form does; a scoped role keeps what it already had.
      const body = ORG_WIDE_ROLES.has(role)
        ? { role, branch_ids: branches.map(b => b.id), app_scope: [] }
        : { role }
      replace(await teamApi.update(m.id, body))
      toast.success(t('team.roleChanged', '{{name}} is now {{role}}.', {
        name: m.name, role: roles.find(r => r.value === role)?.label ?? role,
      }))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('team.saveFailed', 'Could not save that change.'))
    } finally {
      setBusyId(null)
    }
  }

  const roleLabel = (v: string) =>
    v === 'owner' ? t('users.roles.owner', 'Owner') : roles.find(r => r.value === v)?.label ?? v

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Search + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[10rem] flex-1">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder={t('common.search', 'Search')} className="h-9 ps-8" />
        </div>
        {offerAddCashier && canInvite && (
          <Button size="sm" className="h-9 gap-1.5" onClick={() => setAdding(true)} data-testid="cashier-add">
            <Plus className="h-3.5 w-3.5" /> {t('pos.settings.addCashier', 'Add cashier')}
          </Button>
        )}
        {canInvite && showFullPageLink && (
          <Button size="sm" variant="outline" className="h-9 gap-1.5" asChild>
            <a href={`${MY_USERS_URL}?invite=1`}>
              <Mail className="h-3.5 w-3.5" /> {t('team.invite', 'Invite')}
            </a>
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1.5" role="tablist">
        {([
          ['all', t('team.filterAll', 'Everyone')],
          ['till', t('team.filterTill', 'On the till')],
          ['inactive', t('team.filterInactive', 'Switched off')],
        ] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key} type="button" role="tab" aria-selected={filter === key}
            onClick={() => setFilter(key)}
            className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition ${
              filter === key
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:text-foreground'}`}
          >
            {label}
            <span className="tabular-nums opacity-70">{counts[key]}</span>
          </button>
        ))}
      </div>

      {/* Members */}
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded-xl border border-border bg-card">
        {members === null ? (
          [0, 1, 2, 3].map(i => (
            <li key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-44" /></div>
            </li>
          ))
        ) : visible.length === 0 ? (
          <li className="p-8 text-center text-sm text-muted-foreground">
            {t('team.empty', 'Nobody here.')}
          </li>
        ) : visible.map(m => {
          const self = m.id === me?.id
          const locked = self || m.role === 'owner'
          const busy = busyId === m.id
          return (
            <li key={m.id} className={`space-y-2 p-3 ${m.is_active ? '' : 'opacity-60'}`}
                data-testid="member-row" data-name={m.name}>
              <div className="flex items-start gap-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted">
                  <UserCircle2 className="h-4 w-4 text-primary" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{m.name}</span>
                    {self && <Badge variant="outline" className="text-[10px]">{t('team.you', 'You')}</Badge>}
                    {!m.is_active && (
                      <Badge variant="secondary" className="text-[10px]">{t('common.inactive', 'Inactive')}</Badge>
                    )}
                    {canOpenTill(m) && !m.has_pos_pin && (
                      <Badge variant="secondary" className="text-[10px] text-destructive">{t('pos.settings.noPin', 'No PIN')}</Badge>
                    )}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {m.placeholder_email ? t('pos.settings.pinOnly', 'PIN only · no email') : m.email}
                  </p>
                </div>
                {busy
                  ? <Loader2 className="m-2 h-4 w-4 animate-spin text-muted-foreground" />
                  : <MemberActions
                      member={m}
                      onPin={canEdit ? () => setPinFor(m) : undefined}
                      onDiscountChanged={canEdit && canOpenTill(m)
                        ? next => setMembers(list => list && list.map(x => x.id === next.id ? next : x))
                        : undefined}
                    />}
              </div>

              {/* Quick controls: role + on/off. Read-only where a quick change
                  would be unsafe (see the note at the top). */}
              <div className="flex flex-wrap items-center gap-2 ps-11">
                {canEdit && !locked && !ORG_WIDE_ROLES.has(m.role) ? (
                  <Select value={m.role} onValueChange={v => void setRole(m, v)} disabled={busy}>
                    <SelectTrigger className="h-7 w-auto min-w-[9rem] text-xs" aria-label={t('users.role', 'Role')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="text-[10px]">{roleLabel(m.role)}</Badge>
                )}
                {canEdit && !locked && (
                  <label className="ms-auto flex items-center gap-2 text-xs text-muted-foreground">
                    {m.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                    <Switch checked={m.is_active} disabled={busy}
                            onCheckedChange={v => void setActive(m, v)}
                            aria-label={t('team.toggleActive', 'Can sign in')} />
                  </label>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {showFullPageLink && (
        <a href={MY_USERS_URL}
           className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
          {t('team.fullPage', 'Branches, app access, invites and removal — open Users')}
          <ExternalLink className="h-3 w-3" />
        </a>
      )}

      <PosPinDialog
        user={pinFor}
        open={pinFor !== null}
        onOpenChange={v => { if (!v) setPinFor(null) }}
        // Re-read rather than guess: the dialog can also clear a PIN.
        onSaved={() => void load()}
      />
      {offerAddCashier && (
        <AddCashierDialog open={adding} onOpenChange={setAdding} branches={branches} onCreated={() => void load()} />
      )}
    </div>
  )
}
